import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity, ArrowRight, BadgeDollarSign, BarChart3, CalendarClock, ClipboardPaste, FileUp, Landmark,
  LayoutDashboard, ListChecks, Mail, PiggyBank, Route, Sparkles, Timer, TrendingUp, Users, Wallet,
} from 'lucide-react';
import NeverOverpayNav from './NeverOverpayNav';
import { neveroverpayApi } from './neveroverpayApi';
import { ledgerLabel, workflowLabel } from './neveroverpayCopy';
import ConsentGate from './NeverOverpayConsent';
import NeverOverpayTimeline from './NeverOverpayTimeline';
import RecoveryTracker, { TrackerStages } from './RecoveryTracker';
import GmailConnectCard from './GmailConnectCard';
import WeeklyDigestPanel from './WeeklyDigestPanel';
import {
  ACCENT, AMBER, BLUE, MUTED, ROSE, BarChart, Donut, Funnel, MoneyCount, ProgressBar, Reveal, Sparkline, fadeUp, parseMoney, relativeTime, stagger,
} from './npUi';
import './neveroverpay.css';
import './neveroverpay-ui.css';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'cases', label: 'Recoveries', icon: ListChecks },
  { id: 'tracker', label: 'Tracker', icon: Route },
  { id: 'ledger', label: 'Money saved', icon: PiggyBank },
  { id: 'digest', label: 'Weekly digest', icon: Mail },
];

function StatCard({ label, value, foot, icon: Icon, hero = false, spark, color = ACCENT, testId, delay = 0 }) {
  return (
    <motion.div variants={fadeUp} className={`np-card np-stat-card np-lift ${hero ? 'hero' : ''}`} data-testid={testId}>
      <div className="np-stat-label">{Icon ? <Icon size={14} /> : null}{label}</div>
      <div className="np-stat-value">
        {typeof value === 'string' && value.startsWith('$') ? <MoneyCount display={value} /> : value}
      </div>
      {foot ? <div className="np-stat-foot">{foot}</div> : null}
      {spark && spark.some((v) => v > 0) ? <Sparkline points={spark} color={color} height={44} /> : null}
    </motion.div>
  );
}

function SectionTitle({ kicker, title, aside }) {
  return (
    <div className="np-between" style={{ marginBottom: 14 }}>
      <div>
        {kicker ? <div className="np-kicker">{kicker}</div> : null}
        <h2 style={{ fontSize: 20, letterSpacing: '-0.03em', margin: '2px 0 0' }}>{title}</h2>
      </div>
      {aside}
    </div>
  );
}

export default function NeverOverpayApp({ tab = 'cases' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialTab = tab === 'ledger' ? 'ledger' : (params.get('tab') || 'overview');
  const [view, setView] = useState(TABS.some((t) => t.id === initialTab) ? initialTab : 'overview');
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [gmail, setGmail] = useState(null);
  const [plaid, setPlaid] = useState(null);
  const [consents, setConsents] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState('');
  const [invite, setInvite] = useState('');
  const [loaded, setLoaded] = useState(false);
  const autoConnect = params.get('connect') === 'gmail';

  useEffect(() => {
    if (tab === 'ledger') setView('ledger');
  }, [tab]);

  const refresh = useCallback(async () => {
    try {
      const [cases, led, mail, bank, grants, stats, track] = await Promise.all([
        neveroverpayApi.listCases(),
        neveroverpayApi.ledger(),
        neveroverpayApi.gmailStatus().catch(() => null),
        neveroverpayApi.plaidStatus().catch(() => null),
        neveroverpayApi.consents().catch(() => ({ active: [] })),
        neveroverpayApi.analytics().catch(() => null),
        neveroverpayApi.tracker().catch(() => null),
      ]);
      setData(cases);
      setLedger(led);
      setGmail(mail);
      setPlaid(bank);
      setConsents(grants);
      setAnalytics(stats);
      setTracker(track);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load your recoveries');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const live = data?.scan?.status === 'deep_running' || data?.scan?.deep?.status === 'running';
    if (!live) return undefined;
    const timer = setInterval(() => { refresh().catch(() => {}); }, 4000);
    return () => clearInterval(timer);
  }, [data?.scan?.status, data?.scan?.deep?.status, refresh]);

  function switchTab(id) {
    setView(id);
    const next = id === 'ledger' ? '/ledger' : `/app${id === 'overview' ? '' : `?tab=${id}`}`;
    navigate(next, { replace: true });
  }

  async function runConsent(kind) {
    setBusy(true);
    try {
      const grants = await neveroverpayApi.grantConsent(kind);
      setConsents(grants);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await neveroverpayApi.upload(file);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  }

  async function onBankUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const row = await neveroverpayApi.upload(file);
      await refresh();
      const n = row.verified_cases?.length || 0;
      setError(n ? `Matched ${n} recovery from your statement.` : '');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  }

  async function onPaste(event) {
    event.preventDefault();
    if (!paste.trim()) return;
    setBusy(true);
    try {
      const row = await neveroverpayApi.ingestText(paste);
      setPaste('');
      await refresh();
      const n = (row.cases || []).length;
      setError(n ? '' : 'Email read. Nothing qualifies yet — we open a case only when the dates and amounts line up.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function allowGmailSend() {
    setBusy(true);
    try {
      await neveroverpayApi.grantConsent('gmail_send');
      const row = await neveroverpayApi.gmailConnectUrl(true);
      window.location.href = row.url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  const cases = data?.ranked?.length ? data.ranked : (data?.cases || []).filter((c) => c.state !== 'RESOLVED');
  const allCases = data?.cases || [];
  const active = cases.filter((c) => c.state !== 'RESOLVED');
  const rest = allCases.filter((c) => c.state !== 'RESOLVED' && !active.some((row) => row.id === c.id));
  const summary = ledger?.summary || data?.ledger || {};
  const stats = analytics?.summary || {};
  const monthly = analytics?.monthly || [];
  const trackerById = useMemo(() => {
    const map = {};
    (tracker?.items || []).forEach((item) => { map[item.case_id] = item; });
    return map;
  }, [tracker]);
  const needsYou = (tracker?.items || []).filter((t) => t.status === 'active' && t.needs_you).slice(0, 4);
  const firstName = (data?.greeting || '').split('.')[0];

  const outcomeSegments = (analytics?.outcomes || []).map((o) => ({
    label: o.label,
    value: o.count,
    color: o.won ? ACCENT : o.outcome === 'USER_DISMISSED' ? MUTED : ROSE,
  }));
  const bucketSegments = [
    { label: 'Verified', value: summary.verified_cents || 0, color: ACCENT },
    { label: 'Pending', value: summary.pending_cents || 0, color: AMBER },
    { label: 'Potential', value: summary.potential_cents || 0, color: BLUE },
  ];

  const addEmailCard = (
    <Reveal className="np-card" data-testid="add-email-card">
      <SectionTitle kicker="Add proof" title="Add a real email or statement" />
      <p className="np-muted" style={{ marginTop: -6 }}>
        Paste or upload a refund, renewal, or delivery email. Upload a bank CSV so money is counted only when a deposit matches.
        {gmail?.forwarding_address ? <> Or forward company mail to <strong>{gmail.forwarding_address}</strong>.</> : null}
      </p>
      <ConsentGate kind="documents" consents={consents} busy={busy} actionLabel="Allow and continue" onAllow={() => runConsent('documents')}>
        <form onSubmit={onPaste} style={{ marginTop: 14 }}>
          <textarea className="np-input" rows={4} placeholder="Paste a company email here… (refund issued, renewal notice, delivery update)" value={paste} onChange={(e) => setPaste(e.target.value)} data-testid="paste-email-input" />
          <div className="np-actions" style={{ marginTop: 10 }}>
            <button type="submit" className="np-btn np-btn-icon" disabled={busy || !paste.trim()} data-testid="paste-email-submit"><ClipboardPaste size={15} /> Check this email</button>
            <label className="np-btn ghost np-btn-icon">
              <FileUp size={15} /> Upload .eml / PDF / image
              <input type="file" accept=".txt,.eml,.md,.pdf,.html,.png,.jpg,.jpeg,.webp" hidden onChange={onUpload} />
            </label>
          </div>
        </form>
      </ConsentGate>
      <ConsentGate kind="bank_match" consents={consents} busy={busy} actionLabel="Allow bank matching" onAllow={() => runConsent('bank_match')}>
        <div className="np-actions">
          <label className="np-btn ghost np-btn-icon">
            <Landmark size={15} /> Upload bank CSV
            <input type="file" accept=".csv,.ofx,.qfx" hidden onChange={onBankUpload} />
          </label>
          {plaid?.connected ? <span className="np-tag green">Bank connected</span> : null}
        </div>
      </ConsentGate>
    </Reveal>
  );

  const caseCard = (c, index) => {
    const card = c.dashboard || {};
    const detail = card.detail || c.why_now;
    const merchant = card.merchant || c.merchant;
    const track = trackerById[c.id];
    return (
      <motion.div variants={fadeUp} className="np-case-card" key={c.id} data-testid="neveroverpay-ranked-case">
        <div>
          <div className="np-row">
            {typeof index === 'number' ? <span className="np-tag">#{index + 1}</span> : null}
            <span className={`np-tag ${track?.needs_you ? 'amber' : 'blue'}`}>{track?.needs_you ? 'Needs your OK' : workflowLabel(c.workflow)}</span>
            {merchant ? <span className="np-muted">{merchant}</span> : null}
          </div>
          <h3>{c.title || `${workflowLabel(c.workflow)} — ${c.estimated_display}`}</h3>
          <p className="np-muted" style={{ margin: 0 }}>{detail}</p>
          {c.workflow === 'PRICE_INCREASE' && (card.annualized_display || c.annualized_display) ? (
            <p className="np-muted" style={{ margin: '4px 0 0' }}>Estimated annual impact: {card.annualized_display || c.annualized_display}</p>
          ) : null}
          {track ? (
            <div className="np-mini-track" aria-label={`Stage: ${track.stage_label}`}>
              {track.stages.map((s) => <i key={s.id} className={s.done ? 'done' : s.current ? 'now' : ''} />)}
            </div>
          ) : null}
        </div>
        <div>
          <div className="np-amount np-money">{c.estimated_display}</div>
          {track ? <div className="np-muted" style={{ textAlign: 'right', fontSize: 12, marginTop: 2 }}>{track.stage_label}</div> : null}
          <button type="button" className="np-btn np-btn-sm np-btn-icon" style={{ marginTop: 10, width: '100%' }} onClick={() => navigate(`/cases/${c.id}`)}>
            Review <ArrowRight size={14} />
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="np-root" data-testid="neveroverpay-app">
      <div className="np-wrap">
        <NeverOverpayNav mark={view === 'ledger' ? 'Money saved' : 'Home'} />

        <div className="np-dash-head">
          <div>
            <div className="np-kicker">Your BilledTwice AI results</div>
            <h1>{firstName || 'Welcome back'}.</h1>
            <p className="np-muted" style={{ margin: 0 }}>
              Verified, pending, and potential stay separate — we never add them together.
            </p>
          </div>
          <div className="np-tabs" role="tablist" aria-label="Dashboard sections">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button" role="tab" aria-selected={view === id} className={`np-tab ${view === id ? 'on' : ''}`} onClick={() => switchTab(id)} data-testid={`dash-tab-${id}`}>
                {view === id ? <motion.span layoutId="np-tab-pill" className="np-tab-pill" transition={{ type: 'spring', stiffness: 400, damping: 32 }} /> : null}
                <Icon size={15} /> {label}
                {id === 'cases' && active.length ? <span className="np-count">{active.length}</span> : null}
                {id === 'tracker' && tracker?.counts?.needs_you ? <span className="np-count">{tracker.counts.needs_you}</span> : null}
              </button>
            ))}
          </div>
        </div>

        {error ? <div className={error.startsWith('Matched') || error.startsWith('Email read') ? 'np-muted' : 'np-error'} style={{ marginBottom: 16 }} data-testid="dashboard-notice">{error}</div> : null}
        {plaid?.consent_warning ? (
          <div className="np-error" style={{ marginBottom: 16 }}>{plaid.consent_warning} <a href="/settings">Reconnect bank</a></div>
        ) : null}
        {data?.scan?.status === 'deep_running' || data?.scan?.deep?.status === 'running' ? (
          <div className="np-card np-accent" style={{ marginBottom: 16 }}>
            <div className="np-row"><span className="np-pulse-dot" /><strong>Still improving your savings map</strong></div>
            <p className="np-muted" style={{ margin: '6px 0 0' }}>First results are ready. A deeper Gmail and bank pass is still linking refunds, recurring charges, and duplicates in the background.</p>
          </div>
        ) : null}

        <AnimatePresence mode="wait">
          <motion.div key={view} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28 }}>
            {/* ------------------------------------------------------------- OVERVIEW */}
            {view === 'overview' ? (
              <div style={{ display: 'grid', gap: 20 }} data-testid="overview-section">
                <motion.div className="np-grid-4" variants={stagger} initial="hidden" animate={loaded ? 'show' : 'hidden'}>
                  <StatCard hero label="Verified recovered this year" value={summary.headline_display || '$0.00'} icon={BadgeDollarSign} testId="neveroverpay-verified-year"
                    foot={stats.verified_this_week_cents ? `${stats.verified_this_week_display} landed this week` : 'Bank-confirmed money only'} spark={monthly.map((m) => m.verified_cents)} />
                  <StatCard label="Pending with companies" value={summary.pending_display || '$0.00'} icon={Timer} testId="neveroverpay-pending-cases"
                    foot={`${summary.pending_recovery_cases || 0} request${(summary.pending_recovery_cases || 0) === 1 ? '' : 's'} out`} spark={monthly.map((m) => m.pending_cents)} color={AMBER} />
                  <StatCard label="Potential found" value={summary.potential_display || '$0.00'} icon={Sparkles} testId="neveroverpay-potential"
                    foot={`${stats.cases_open || 0} open · ${stats.found_this_week || 0} new this week`} spark={monthly.map((m) => m.found)} color={BLUE} />
                  <StatCard label="Annual recurring saved" value={`${summary.verified_annual_display || '$0.00'}`} icon={TrendingUp} testId="neveroverpay-verified-annual"
                    foot={`${summary.upcoming_renewals || 0} renewal${(summary.upcoming_renewals || 0) === 1 ? '' : 's'} coming up`} />
                </motion.div>

                <div className="np-grid-3">
                  <Reveal className="np-card np-lift">
                    <div className="np-stat-label"><ListChecks size={14} /> Needs your OK</div>
                    <div className="np-stat-value" style={{ fontSize: 26 }}>{stats.needs_you || 0}</div>
                    <ProgressBar value={stats.cases_open ? ((stats.needs_you || 0) / stats.cases_open) * 100 : 0} color={AMBER} />
                    <div className="np-stat-foot" style={{ marginTop: 8 }}>of {stats.cases_open || 0} open recoveries</div>
                  </Reveal>
                  <Reveal className="np-card np-lift" delay={0.05}>
                    <div className="np-stat-label"><Activity size={14} /> Recovery rate</div>
                    <div className="np-stat-value" style={{ fontSize: 26 }}>{stats.recovery_rate_pct === null || stats.recovery_rate_pct === undefined ? '—' : `${stats.recovery_rate_pct}%`}</div>
                    <ProgressBar value={stats.recovery_rate_pct || 0} />
                    <div className="np-stat-foot" style={{ marginTop: 8 }}>{stats.cases_won || 0} won · {stats.cases_closed || 0} closed</div>
                  </Reveal>
                  <Reveal className="np-card np-lift" delay={0.1}>
                    <div className="np-stat-label"><CalendarClock size={14} /> Avg. days to money back</div>
                    <div className="np-stat-value" style={{ fontSize: 26 }}>{stats.avg_days_to_recover === null || stats.avg_days_to_recover === undefined ? '—' : stats.avg_days_to_recover}</div>
                    <div className="np-stat-foot">{analytics?.sources?.documents || 0} documents read · {analytics?.sources?.transactions || 0} bank lines matched</div>
                  </Reveal>
                </div>

                <div className="np-grid-2">
                  <Reveal className="np-card" data-testid="analytics-monthly">
                    <SectionTitle kicker="Trend" title="Money back by month" aside={<span className="np-tag green">verified</span>} />
                    <BarChart data={monthly} valueKey="verified_cents" secondaryKey="pending_cents" labelKey="label" height={150} emptyLabel="Your first verified deposit will start this chart." />
                    <p className="np-muted" style={{ marginTop: 10 }}>Green = verified by your bank. Grey = pending with the company.</p>
                  </Reveal>
                  <Reveal className="np-card" delay={0.05} data-testid="analytics-funnel">
                    <SectionTitle kicker="Pipeline" title="From found to money back" aside={<button type="button" className="np-btn ghost np-btn-sm" onClick={() => switchTab('tracker')}>Open tracker</button>} />
                    <Funnel steps={analytics?.funnel || []} />
                  </Reveal>
                </div>

                <div className="np-grid-2">
                  <Reveal className="np-card" data-testid="analytics-workflows">
                    <SectionTitle kicker="By problem type" title="What we are recovering" />
                    {(analytics?.by_workflow || []).length === 0 ? (
                      <div className="np-empty"><strong>No recoveries yet</strong>Connect Gmail or paste a company email to see this breakdown.</div>
                    ) : (
                      <div>
                        <div className="np-wf-row head"><span>Type</span><span>Potential</span><span>Pending</span><span>Verified</span><span>Open</span></div>
                        {(analytics?.by_workflow || []).map((row) => (
                          <div className="np-wf-row" key={row.workflow}>
                            <div>
                              <div>{row.label}</div>
                              <ProgressBar value={row.count ? ((row.won || 0) / row.count) * 100 : 0} height={4} className="np-mt" />
                            </div>
                            <strong>{row.potential_display}</strong>
                            <strong style={{ color: AMBER }}>{row.pending_display}</strong>
                            <strong className="np-money">{row.verified_display}</strong>
                            <span className="np-tag">{row.open}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Reveal>
                  <Reveal className="np-card" delay={0.05} data-testid="analytics-buckets">
                    <SectionTitle kicker="Buckets" title="Verified vs pending vs potential" />
                    <div className="np-row" style={{ gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Donut segments={bucketSegments} centerValue={summary.headline_display || '$0.00'} centerLabel="verified" />
                      <ul className="np-legend" style={{ flex: 1, minWidth: 180, padding: 0, margin: 0 }}>
                        {bucketSegments.map((seg) => (
                          <li key={seg.label}><i style={{ background: seg.color }} />{seg.label}<strong>${(seg.value / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></li>
                        ))}
                        {outcomeSegments.length ? (
                          <li style={{ marginTop: 8, color: MUTED }}>Outcomes: {outcomeSegments.map((o) => `${o.value} ${o.label.toLowerCase()}`).join(' · ')}</li>
                        ) : null}
                      </ul>
                    </div>
                  </Reveal>
                </div>

                <div className="np-grid-2" style={{ alignItems: 'start' }}>
                  <GmailConnectCard gmail={gmail} consents={consents} busy={busy} onChanged={refresh} autoStart={autoConnect} />
                  {addEmailCard}
                </div>

                {needsYou.length ? (
                  <Reveal className="np-card np-accent" data-testid="needs-you-section">
                    <SectionTitle kicker="Action" title={`${needsYou.length} request${needsYou.length === 1 ? '' : 's'} waiting on your OK`} aside={<button type="button" className="np-btn ghost np-btn-sm" onClick={() => switchTab('cases')}>See all</button>} />
                    <div style={{ display: 'grid', gap: 10 }}>
                      {needsYou.map((item) => (
                        <div className="np-case-card" key={item.case_id} style={{ padding: 14 }}>
                          <div>
                            <div className="np-row"><span className="np-tag amber">Needs your OK</span><span className="np-muted">{item.workflow_label}</span></div>
                            <h3 style={{ fontSize: 15 }}>{item.title}</h3>
                            <TrackerStages stages={item.stages} status={item.status} compact />
                          </div>
                          <div>
                            <div className="np-amount np-money">{item.amount_display}</div>
                            <button type="button" className="np-btn np-btn-sm np-btn-icon" style={{ marginTop: 10, width: '100%' }} onClick={() => navigate(`/cases/${item.case_id}`)}>Review <ArrowRight size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Reveal>
                ) : null}

                <div className="np-grid-2">
                  <Reveal className="np-card" data-testid="analytics-renewals">
                    <SectionTitle kicker="Coming up" title="Renewals & price changes" />
                    {(analytics?.renewals || []).length === 0 ? (
                      <p className="np-muted">No renewals on the radar. We will flag one as soon as a notice lands in your mail.</p>
                    ) : (analytics?.renewals || []).map((row) => (
                      <button key={row.case_id} type="button" className="np-case-row" style={{ width: '100%', background: 'transparent', color: 'inherit', textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate(`/cases/${row.case_id}`)}>
                        <div>
                          <div className="np-row"><span className={`np-tag ${row.days_left !== null && row.days_left <= 7 ? 'rose' : 'amber'}`}>{row.days_left === null ? 'date unknown' : row.days_left <= 0 ? 'today' : `in ${row.days_left} day${row.days_left === 1 ? '' : 's'}`}</span><span className="np-muted">{row.workflow_label}</span></div>
                          <h3 style={{ marginTop: 6, fontSize: 15 }}>{row.merchant}</h3>
                        </div>
                        <div className="np-stat" style={{ fontSize: 20 }}>{row.amount_display}</div>
                      </button>
                    ))}
                  </Reveal>
                  <Reveal className="np-card" delay={0.05} data-testid="analytics-activity">
                    <SectionTitle kicker="Live" title="Recent activity" aside={<span className="np-row np-muted" style={{ fontSize: 12 }}><span className="np-pulse-dot" /> watching</span>} />
                    {(analytics?.activity || []).length === 0 ? (
                      <p className="np-muted">Activity appears here as we read mail, open recoveries, and match deposits.</p>
                    ) : (
                      <div className="np-activity">
                        {(analytics?.activity || []).slice(0, 8).map((row, index) => (
                          <div className="np-activity-row" key={`${row.case_id}-${row.at}-${index}`}>
                            <span className="np-activity-dot" style={{ background: row.to_state === 'RESOLVED' ? ACCENT : row.to_state === 'READY_FOR_REVIEW' ? AMBER : 'rgba(232,239,230,0.35)' }} />
                            <div>
                              <div>{row.title} <span className="np-muted">→ {row.stage_label}</span></div>
                              <div className="np-muted" style={{ fontSize: 12 }}>{row.merchant}{row.merchant ? ' · ' : ''}{row.amount_display}</div>
                            </div>
                            <time>{relativeTime(row.at)}</time>
                          </div>
                        ))}
                      </div>
                    )}
                  </Reveal>
                </div>

                {(data?.graph?.timeline || []).length ? (
                  <Reveal className="np-card" data-testid="neveroverpay-home-timeline">
                    <SectionTitle kicker="One timeline" title="Mail and bank, together" />
                    <NeverOverpayTimeline timeline={(data.graph.timeline || []).slice(0, 12)} path={[]} />
                  </Reveal>
                ) : null}
              </div>
            ) : null}

            {/* ------------------------------------------------------------- RECOVERIES */}
            {view === 'cases' ? (
              <div style={{ display: 'grid', gap: 20 }} data-testid="cases-section">
                <div className="np-grid-3">
                  <Reveal className="np-card np-stat-card"><div className="np-stat-label"><ListChecks size={14} /> Open recoveries</div><div className="np-stat-value" style={{ fontSize: 26 }}>{stats.cases_open || 0}</div><div className="np-stat-foot">{stats.needs_you || 0} need your OK</div></Reveal>
                  <Reveal className="np-card np-stat-card" delay={0.05}><div className="np-stat-label"><Wallet size={14} /> Worth on the table</div><div className="np-stat-value" style={{ fontSize: 26 }}><MoneyCount display={data?.found_worth_display || summary.potential_display || '$0.00'} /></div><div className="np-stat-foot">potential + pending, never added to verified</div></Reveal>
                  <Reveal className="np-card np-stat-card" delay={0.1}><div className="np-stat-label"><BarChart3 size={14} /> Found this week</div><div className="np-stat-value" style={{ fontSize: 26 }}>{stats.found_this_week || 0}</div><div className="np-stat-foot">from {analytics?.sources?.documents || 0} documents read</div></Reveal>
                </div>
                <div className="np-grid-2" style={{ alignItems: 'start' }}>
                  <GmailConnectCard gmail={gmail} consents={consents} busy={busy} onChanged={refresh} />
                  {addEmailCard}
                </div>
                <div>
                  <SectionTitle kicker="Ranked by value and urgency" title={data?.greeting || (active.length ? `We found ${active.length} ${active.length === 1 ? 'item' : 'items'} worth reviewing.` : 'Opportunities worth reviewing')} />
                  {active.length === 0 ? (
                    <div className="np-empty">
                      <strong>Nothing open yet</strong>
                      {gmail?.connected
                        ? 'Gmail is connected. We only open a case when a refund, upcoming renewal, or late delivery has the dates and amounts we need.'
                        : 'Connect Gmail or paste a company email you actually received.'}
                    </div>
                  ) : (
                    <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'grid', gap: 12 }}>
                      {active.map((c, index) => caseCard(c, index))}
                    </motion.div>
                  )}
                  {rest.length ? <h2 style={{ fontSize: 18, margin: '28px 0 10px' }}>Other open items</h2> : null}
                  <div style={{ display: 'grid', gap: 12 }}>{rest.map((c) => caseCard(c))}</div>
                </div>
                <form
                  className="np-card"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    if (!invite.trim()) return;
                    setBusy(true);
                    try {
                      await neveroverpayApi.inviteMember(invite.trim());
                      setInvite('');
                      setError('');
                    } catch (err) {
                      setError(err.message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <div className="np-row"><div className="np-icon-box blue" style={{ marginBottom: 0 }}><Users size={18} /></div><div><h3 style={{ margin: 0 }}>Household</h3><p className="np-muted" style={{ margin: 0 }}>Invite someone who shares this inbox or bank account. They will need their own login.</p></div></div>
                  <div className="np-actions" style={{ marginTop: 12 }}>
                    <input className="np-input" placeholder="person@email.com" value={invite} onChange={(e) => setInvite(e.target.value)} />
                    <button type="submit" className="np-btn ghost" disabled={busy}>Invite</button>
                  </div>
                </form>
              </div>
            ) : null}

            {/* ------------------------------------------------------------- TRACKER */}
            {view === 'tracker' ? (
              <div data-testid="tracker-section">
                <RecoveryTracker data={tracker} loading={!loaded} />
              </div>
            ) : null}

            {/* ------------------------------------------------------------- LEDGER */}
            {view === 'ledger' ? (
              <div style={{ display: 'grid', gap: 20 }} data-testid="ledger-section">
                <motion.div className="np-grid-4" variants={stagger} initial="hidden" animate="show">
                  <StatCard hero label="Verified recovered" value={summary.headline_display || '$0.00'} icon={BadgeDollarSign} foot="the only number that counts as money back" spark={monthly.map((m) => m.verified_cents)} />
                  <StatCard label="Confirmed by company" value={summary.confirmed_display || '$0.00'} icon={Mail} foot="they said they issued it — bank not yet matched" color={BLUE} />
                  <StatCard label="Pending" value={summary.pending_display || '$0.00'} icon={Timer} foot="approved, waiting on the company" color={AMBER} />
                  <StatCard label="Annual recurring" value={summary.verified_annual_display || '$0.00'} icon={TrendingUp} foot="renewals you stopped, per year" />
                </motion.div>
                <div className="np-grid-2">
                  <Reveal className="np-card">
                    <SectionTitle kicker="Trend" title="Verified by month" />
                    <BarChart data={monthly} valueKey="verified_cents" labelKey="label" height={150} emptyLabel="Nothing verified yet." />
                  </Reveal>
                  <Reveal className="np-card" delay={0.05}>
                    <SectionTitle kicker="Buckets" title="Kept separate on purpose" />
                    <div className="np-row" style={{ gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Donut segments={bucketSegments} centerValue={summary.headline_display || '$0.00'} centerLabel="verified" />
                      <ul className="np-legend" style={{ flex: 1, minWidth: 180, padding: 0, margin: 0 }}>
                        {bucketSegments.map((seg) => (
                          <li key={seg.label}><i style={{ background: seg.color }} />{seg.label}<strong>${(seg.value / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></li>
                        ))}
                        <li style={{ color: MUTED }}>Upcoming renewal risk {summary.upcoming_renewal_display || '$0.00'} · Potential to review {summary.potential_review_display || '$0.00'}</li>
                      </ul>
                    </div>
                  </Reveal>
                </div>
                <Reveal className="np-card">
                  <SectionTitle kicker="Ledger" title="Every counted line" />
                  {(ledger?.events || []).length === 0 ? <div className="np-empty"><strong>Nothing counted yet</strong>Verified lines appear here when a bank deposit matches a recovery.</div> : null}
                  {(ledger?.events || []).map((row) => (
                    <div className="np-case-row" key={row.id || `${row.case_id}-${row.state}`}>
                      <div>
                        <span className={`np-tag ${row.state === 'verified' || row.state === 'recurring_verified' ? 'green' : row.state === 'pending' ? 'amber' : ''}`}>{ledgerLabel(row.state)}</span>
                        <div style={{ marginTop: 8 }}>{workflowLabel(row.workflow)}</div>
                        <div className="np-muted" style={{ fontSize: 12 }}>{relativeTime(row.updated_at || row.created_at)}</div>
                      </div>
                      <div className={`np-stat ${row.state === 'verified' || row.state === 'recurring_verified' ? 'np-money' : ''}`} style={{ fontSize: 22 }}>{row.display}</div>
                    </div>
                  ))}
                </Reveal>
              </div>
            ) : null}

            {/* ------------------------------------------------------------- DIGEST */}
            {view === 'digest' ? (
              <div data-testid="digest-section">
                <WeeklyDigestPanel gmail={gmail} onNeedGmailSend={allowGmailSend} />
              </div>
            ) : null}
          </motion.div>
        </AnimatePresence>
        <div style={{ height: 60 }} />
      </div>
    </div>
  );
}

export { parseMoney };
